/** Decouple one output from future sync updates (a reason is required and recorded with who
 *  and when), or re-couple it — the next explicit sync or applied change then rebuilds it.
 *  "Re-couple and preview": before re-coupling, a dry-run build shows what the next update
 *  would overwrite, ops' edits included (`recouple_preview/`; 409 `preview_unsupported` while
 *  the integration has no dry-run build, which `meta.gate.preview_supported` says up front). */
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import * as catalogueApi from '@/api/catalogue';
import type { CatalogueOutput } from '@/api/catalogue';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { formatDate } from '@/utils/format';

import { entityLabel } from './catalogueModel';
import { PreviewResult } from './PreviewResult';
import { useInvalidateCatalogue } from './queries';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export interface CouplingDialogProps {
  tenantId: number;
  output: CatalogueOutput;
  productName: string;
  /** The integration has a dry-run build (`meta.gate.preview_supported`). */
  previewSupported?: boolean;
  onClose: () => void;
}

export function CouplingDialog({
  tenantId,
  output,
  productName,
  previewSupported = false,
  onClose,
}: CouplingDialogProps) {
  const { show } = useSnackbar();
  const invalidate = useInvalidateCatalogue(tenantId);
  const decoupling = output.coupled;
  const [note, setNote] = useState('');
  const preview = useQuery({
    queryKey: ['catalogue', tenantId, 'recouplePreview', output.id],
    queryFn: () => catalogueApi.recouplePreview(tenantId, output.id),
    enabled: !decoupling && previewSupported,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const mutation = useMutation({
    mutationFn: () =>
      catalogueApi.setCoupling(tenantId, output.id, {
        coupled: !decoupling,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: async () => {
      show(decoupling ? 'Decoupled — future syncs leave it alone.' : 'Re-coupled — the next sync rebuilds it.', 'success');
      await invalidate();
      onClose();
    },
  });

  const what = `${entityLabel(output.entity)} ${output.ext_id}`;
  return (
    <AppDialog
      open
      onClose={onClose}
      title={decoupling ? `Decouple ${entityLabel(output.entity).toLowerCase()}` : `Re-couple ${entityLabel(output.entity).toLowerCase()}`}
      size={decoupling ? 'sm' : 'md'}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color={decoupling ? 'warning' : 'primary'}
            disabled={mutation.isPending || (decoupling && !note.trim())}
            onClick={() => mutation.mutate()}
          >
            {decoupling ? 'Decouple' : 'Re-couple'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        {productName} · <Box component="span" sx={{ fontFamily: MONO }}>{what}</Box>
        {output.option_id ? ` · option ${output.option_id}` : ''}
      </Typography>
      {decoupling ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A decoupled {entityLabel(output.entity).toLowerCase()} is left out of every build — explicit
          syncs and upstream changes alike — until it is re-coupled. Edits made to it by hand are kept.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Decoupled
          {output.coupling_changed_by ? ` by ${output.coupling_changed_by.name || output.coupling_changed_by.email}` : ''}
          {output.coupling_changed_ts ? ` on ${formatDate(output.coupling_changed_ts)}` : ''}
          {output.coupling_note ? `: “${output.coupling_note}”` : ''}. Re-coupled, the next explicit
          sync or applied change rebuilds it and overwrites what was edited by hand.
        </Typography>
      )}
      {!decoupling && (
        <Box sx={{ mb: 2 }} aria-live="polite">
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            What the next update would do
          </Typography>
          {!previewSupported ? (
            <Alert severity="info" sx={{ py: 0 }}>
              This integration has no dry-run build yet, so there is no preview. Re-coupling still works.
            </Alert>
          ) : preview.isLoading ? (
            <CircularProgress size={20} aria-label="Building the preview" />
          ) : preview.isError ? (
            <Alert severity={catalogueApi.isPreviewUnsupported(preview.error) ? 'info' : 'error'} sx={{ py: 0 }}>
              {catalogueApi.isPreviewUnsupported(preview.error)
                ? 'This integration has no dry-run build yet, so there is no preview. Re-coupling still works.'
                : (preview.error as Error).message}
            </Alert>
          ) : preview.data ? (
            <PreviewResult preview={preview.data} />
          ) : null}
        </Box>
      )}
      <TextField
        label={decoupling ? 'Reason (required)' : 'Note (optional)'}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        size="small"
        inputProps={{ maxLength: 500 }}
        autoFocus={decoupling}
      />
      {mutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(mutation.error as Error).message}
        </Alert>
      )}
    </AppDialog>
  );
}
